import { Box, Paper, Table, TableBody, TableContainer } from '@mui/material';
import { Z_INDEX } from '@/constants/z-index';
import type { SymbolKey } from '@/entities/symbol';
import { useSelectionFunctions } from '@/features/symbol-selector';
import type { FullScreenLegendSeries } from '../fullScreenChartTypes';
import LegendTableHeader from './LegendTableHeader';
import LegendTableRow from './LegendTableRow';

interface FullScreenLegendProps {
  onToggleSeries: (seriesId: string) => void;
  series: FullScreenLegendSeries[];
}

const FullScreenLegend = ({ onToggleSeries, series }: FullScreenLegendProps) => {
  const {
    addSymbolToSelection,
    isSymbolSelected,
    removeSymbolFromSelection,
    isSpotLocked,
    toggleSymbolInSelection
  } = useSelectionFunctions();
  const minTableHeight = series.length * 25 + 50;

  const handleLockedClick = (symbol: SymbolKey | undefined): void => {
    if (symbol) {
      toggleSymbolInSelection(symbol);
    }
  };

  const handleSelectionChange = (symbol: SymbolKey | undefined, selected: boolean): void => {
    if (!symbol) return;

    if (selected) {
      removeSymbolFromSelection(symbol);
      return;
    }

    addSymbolToSelection(symbol);
  };

  return (
    <Box
      aria-label='Chart series legend'
      component='section'
      sx={{ pt: 1, pr: 3 }}
    >
      <TableContainer
        component={Paper}
        sx={{ width: '100%', pl: '78px', maxHeight: 155, pr: 1 }}
      >
        <Table
          aria-label='Chart series legend table'
          size='small'
          sx={{
            minWidth: 600,
            minHeight: minTableHeight,
            thead: {
              top: 0,
              zIndex: Z_INDEX.chartOverlay
            }
          }}
        >
          <LegendTableHeader />
          <TableBody sx={{ maxHeight: 150 }}>
            {series.map(item => (
              <LegendTableRow
                key={item.id}
                isSpotLocked={Boolean(isSpotLocked)}
                isSymbolSelected={isSymbolSelected}
                item={item}
                onLockedClick={handleLockedClick}
                onSelectionChange={handleSelectionChange}
                onToggleSeries={onToggleSeries}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FullScreenLegend;
